import { config } from '@/configs'
import { AppSideMenu } from '@/domains/appShell/sideMenu/AppSideMenu'
import { userPermissionsAtom } from '@/domains/appShell/stores/user.store'
import { UserInfoRole } from '@/domains/appShell/types/userRole.type'
import Unauthorized from '@/domains/appShell/unauthorized/Unauthorized'
import { DevMocksBadge } from '@/domains/testing/devMocks/DevMocksBadge'
import { TestingPanel } from '@/domains/testing/testingPanel/TestingPanel'

import type { FC } from 'react'
import { Navigate, Outlet, type RouteObject, useLocation, useParams } from 'react-router-dom'

import { useAtomValue } from 'jotai'

import { getCurrentRoute, getFirstAccessibleTargetPath } from './layout.utils'

/**
 * Route definition: a React Router `RouteObject` extended with navigation and security metadata.
 */
export type Route = {
  /** Unique identifier of the route */
  pathname: string
  /** `pathname` of the parent route: nests this entry as a SideMenu sub-item */
  childOf?: string
  /** i18n key of the SideMenu entry (routes without a label are not listed) */
  label?: string
  /**
   * Additional path prefixes that keep this top-level SideMenu entry highlighted, beyond the exact
   * `to`/`end` NavLink match. Needed for a nav entry anchored at `/` (e.g. Onboardings) whose owned
   * pages live elsewhere (`/onboardings/:id`, `/onboarding/aml/:id`…): without this the item is only
   * active on the exact home path. A page is active when its pathname starts with any prefix.
   */
  activePathPrefixes?: string[]
  /**
   * On a SideMenu group (a route without `path`): when exactly one of its children is accessible to
   * the user, the group renders as a plain `SideMenu.Item` pointing to that child instead of an
   * expandable group. Opt-in, so a group made of a single `childOf` child keeps rendering as a group
   * by default.
   */
  collapseWhenSoleChild?: boolean
  /**
   * Hides the SideMenu navigation entries when this route is the user's only accessible entry: a
   * user who can reach nothing else lands on it (guard redirect) and gets a shell with the logo and
   * the logout action only. Opt-in per route, so existing single-page profiles keep their menu.
   */
  hideNavigationWhenSoleEntry?: boolean
  /** Permission guard */
  guard?: {
    /** Permission required to access the route */
    permission: string
    /** Role(s) required for this permission (all roles by default) */
    roles?: UserInfoRole | UserInfoRole[]
  }
} & RouteObject

type LayoutProps = {
  /** Route configuration, used for both the SideMenu and the permission guard */
  routes: Route[]
}

/**
 * Application shell: the Design System SideMenu, the routed page, and the Testing Panel in
 * `testingTooling` environments.
 *
 * The SideMenu is `position: fixed` (a 52px rail on desktop, a hidden drawer on mobile), so `main`
 * is offset by the rail width from `lg` up. The `has-side-menu` marker also lets fixed bars
 * (`.toolbar-root`) stop at the rail instead of sliding under it (see `sideMenu.css`).
 *
 * Every page goes through `LayoutGuard`, which checks the permission of the current route.
 */
export const Layout: FC<LayoutProps> = ({ routes }) => (
  <div className="scrollbar-hide relative flex h-screen w-screen flex-col">
    <AppSideMenu routes={routes} />
    {/* 52px must stay aligned with `--side-menu-rail-width` (src/shared/ui/sideMenu/sideMenu.css). */}
    <main className="scrollbar-hide has-side-menu lg:pl-[52px]">
      <LayoutGuard routes={routes} />
    </main>
    {config.testingTooling && <TestingPanel />}
    {config.testingTooling && <DevMocksBadge />}
  </div>
)

/**
 * Checks the permission of the current route. Without it, redirects to the first reachable
 * top-level route (the first accessible child of a SideMenu group, which has no `path`), or
 * renders the Unauthorized screen when there is none.
 */
const LayoutGuard: FC<{ routes: Route[] }> = ({ routes }) => {
  const location = useLocation()
  const { hasPermission } = useAtomValue(userPermissionsAtom)
  const { uuid } = useParams()

  const currentRoute = getCurrentRoute(routes, location.pathname, uuid)

  if (currentRoute?.guard) {
    const { permission, roles } = currentRoute.guard
    const hasAccess = hasPermission(permission, roles)

    if (!hasAccess) {
      const targetPath = getFirstAccessibleTargetPath(routes, hasPermission)

      if (targetPath) {
        return <Navigate to={targetPath} replace />
      }

      return <Unauthorized />
    }
  }

  return <Outlet />
}
